import type { RolePermissionsGroupedViewModel } from '@mehir/api/backoffice-api';

/**
 * A single permission a role holds, flattened out of the grouped response.
 * Structurally identical to the API's RuntimePermissionGrantDto, but declared
 * here because we build these ourselves rather than reading them off one
 * specific endpoint.
 */
export interface PermissionGrant {
  moduleName?: string | null;
  subModuleName?: string | null;
  scopeName?: string | null;
}

/** Scope names the permission matrix exposes for every sub-module. */
export const SCOPE = {
  view: 'View',
  create: 'Create',
  update: 'Update',
  delete: 'Delete',
} as const;

export type Scope = (typeof SCOPE)[keyof typeof SCOPE];

export interface PermissionRequirement {
  module: string;
  /** Omit to accept any sub-module inside the module. */
  subModule?: string;
  /** Omit to accept any scope. */
  scope?: Scope;
}

const same = (a: string | null | undefined, b: string | null | undefined) =>
  (a ?? '').trim().toLowerCase() === (b ?? '').trim().toLowerCase();

/**
 * A grant satisfies a requirement when every part the requirement names
 * matches. Parts the requirement leaves out are wildcards, so `{module: 'Fit'}`
 * is met by any grant inside Fit.
 */
export const grantSatisfies = (
  grant: PermissionGrant,
  requirement: PermissionRequirement
): boolean => {
  if (!same(grant.moduleName, requirement.module)) return false;
  if (requirement.subModule && !same(grant.subModuleName, requirement.subModule)) return false;
  if (requirement.scope && !same(grant.scopeName, requirement.scope)) return false;
  return true;
};

export const canAccess = (
  grants: PermissionGrant[],
  requirement: PermissionRequirement | null | undefined
): boolean => {
  // No requirement declared for this screen — nothing to enforce.
  if (!requirement) return true;
  return grants.some((grant) => grantSatisfies(grant, requirement));
};

/** Removes duplicates so repeated grants across several roles collapse. */
export const mergeGrants = (
  grantLists: (PermissionGrant[] | null | undefined)[]
): PermissionGrant[] => {
  const seen = new Map<string, PermissionGrant>();
  for (const grants of grantLists) {
    for (const grant of grants ?? []) {
      const key = [grant.moduleName, grant.subModuleName, grant.scopeName]
        .map((part) => (part ?? '').trim().toLowerCase())
        .join('|');
      if (!seen.has(key)) seen.set(key, grant);
    }
  }
  return [...seen.values()];
};

/**
 * Flattens the grouped role-permission response into individual grants.
 *
 * The panel reads permissions from /Permissions/role-permissions/{role} — the
 * same endpoint the role editor uses — rather than the runtime authorization
 * endpoint, which currently answers 400 with an EF Core translation failure.
 * Both describe the same thing; only the shape differs.
 */
export const flattenRolePermissions = (
  grouped: RolePermissionsGroupedViewModel | null | undefined
): PermissionGrant[] => {
  const grants: PermissionGrant[] = [];
  for (const module of grouped?.modules ?? []) {
    for (const subModule of module.subModules ?? []) {
      for (const scope of subModule.scopes ?? []) {
        grants.push({
          moduleName: module.moduleName,
          subModuleName: subModule.subModuleName,
          scopeName: scope.scopeName,
        });
      }
      // A sub-module with no scopes still proves module-level access.
      if (!subModule.scopes?.length) {
        grants.push({ moduleName: module.moduleName, subModuleName: subModule.subModuleName });
      }
    }
    if (!module.subModules?.length) {
      grants.push({ moduleName: module.moduleName });
    }
  }
  return grants;
};
